import uiConfig from "../../ui-config";
import { AnalyticsProvider } from "./analytics-provider";

declare let mixpanel: any;

export async function initializeMixpanel(mixpanelObject?: Record<string, any>): Promise<void> {
  if (mixpanelObject && typeof mixpanelObject.init === "function" && uiConfig.mixpanelToken) {
    // Browser: use the client provided and set it in global scope
    (window as any).mixpanel = mixpanelObject;
    // The browser client doesn't allow callbacks for initialization
    mixpanel.init(uiConfig.mixpanelToken);
  } else if (mixpanel && uiConfig.mixpanelToken) {
    // Mobile: use the callbacks and return a promise
    return new Promise<void>((resolve, reject) => {
      mixpanel.init(
        uiConfig.mixpanelToken,
        () => resolve(),
        () => reject(),
      );
    });
  }

  return Promise.resolve();
}

export class MixpanelAnalytics implements AnalyticsProvider {
  public name = "Mixpanel";

  private currentScreenName = "";

  public get enabled(): boolean {
    return typeof mixpanel !== "undefined" && !!uiConfig.mixpanelToken;
  }

  public identify(userId: string): void {
    mixpanel.identify(userId);
  }

  public action(actionName: string, featureName: string, extraData?: Record<string, any>): void {
    const extraDataWithFeatureName = extraData ?? {};
    extraDataWithFeatureName.featureName = featureName;
    mixpanel.track(actionName, extraDataWithFeatureName);
  }

  public actionAsync(
    actionName: string,
    featureName: string,
    extraData?: Record<string, any>,
  ): Promise<void> {
    const extraDataWithFeatureName = extraData ?? {};
    extraDataWithFeatureName.featureName = featureName;
    return new Promise<void>((resolve, reject) => {
      try {
        mixpanel.track(actionName, extraDataWithFeatureName, {}, (response: 0 | 1) => {
          if (response === 1) {
            resolve();
          } else {
            reject(response);
          }
        });
      } catch (err) {
        reject(err);
      }
    });
  }

  public event(eventName: string, extraData?: Record<string, any>): void {
    mixpanel.track(eventName, extraData);
  }

  public eventAsync(eventName: string, extraData?: Record<string, any>): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      mixpanel.track(eventName, extraData, {}, (err) => {
        if (err) {
          reject(err);
        } else {
          resolve();
        }
      });
    });
  }

  screen(screenName: string): void {
    this.currentScreenName = screenName;
    mixpanel.registerSuperProperties({
      "Current Screen": screenName,
    });
  }

  async logout(): Promise<void> {
    mixpanel.reset();
  }
}
